/**
 * AdoraCare - the front-desk CLINIC LINKS bar
 * (DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1, item 2).
 *
 * TWO one-click doors at the visit-close moment: open the org's Google review
 * page / Instagram profile in a new tab, or copy the link to hand to the
 * patient. Rendered ONLY on the checkout completion surface (the billing
 * "Payment recorded" receipt) -- the moment the tenant actually makes the ask.
 *
 * RENDERS NOTHING when the org has configured no links (buildClinicLinkEntries
 * returns []) -- never an empty shell, never a disabled button (no dead doors,
 * LES-016). Role scope is the hosting surface's existing audience; this
 * component adds no gate of its own and grants nothing.
 *
 * NO patient messaging (two-channel rule): copy + open only.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): the pure projection
 * lives in clinic-links-view.ts; this file renders from a `me` payload and owns
 * only the transient "copied" acknowledgement. `navigator.clipboard` is
 * feature-detected (the LeadContactReveal precedent) so a browser without it
 * simply keeps the open door working.
 */

import { useState } from 'react';

import { acButtonClass } from '@/dashboard/ui';
import type { DashboardMe } from '@/dashboard/types';
import {
  buildClinicLinkEntries,
  CLINIC_LINKS_HINT,
  CLINIC_LINKS_TITLE,
  CLINIC_LINK_COPIED,
  CLINIC_LINK_COPY_ACTION,
  type ClinicLinkId,
} from '@/dashboard/clinic-links/clinic-links-view';

export default function ClinicLinksBar({
  me,
}: {
  readonly me: DashboardMe | null;
}): JSX.Element | null {
  const [copied, setCopied] = useState<ClinicLinkId | null>(null);
  const entries = buildClinicLinkEntries(me);
  if (entries.length === 0) return null;

  return (
    <section className="clinic-links" data-testid="clinic-links-bar">
      <h3 className="clinic-links__title">{CLINIC_LINKS_TITLE}</h3>
      <p className="clinic-links__hint">{CLINIC_LINKS_HINT}</p>
      <div className="clinic-links__actions">
        {entries.map((entry) => (
          <span className="clinic-links__pair" key={entry.id}>
            <a
              className={acButtonClass('secondary', 'sm')}
              href={entry.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`clinic-link-open-${entry.id}`}
            >
              {entry.openLabel}
            </a>
            <button
              type="button"
              className={acButtonClass('secondary', 'sm')}
              data-testid={`clinic-link-copy-${entry.id}`}
              onClick={() => {
                const nav: Navigator | undefined =
                  typeof navigator === 'undefined' ? undefined : navigator;
                if (nav?.clipboard?.writeText !== undefined) {
                  void nav.clipboard.writeText(entry.href);
                }
                setCopied(entry.id);
              }}
            >
              {copied === entry.id
                ? CLINIC_LINK_COPIED
                : CLINIC_LINK_COPY_ACTION}
            </button>
          </span>
        ))}
      </div>
    </section>
  );
}
